import type {
  GetUserProfileResponse,
  ProfileCounts,
  ProfileRelationship,
  UserFavoriteSummary,
} from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';
import { formatUsername } from '@fetmode/utils';
import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth, useAuthModal } from '../auth/index.js';
import { ProfilePostsPanel } from './ProfilePostsPanel.js';
import './MemberProfilePage.css';

interface MemberProfilePageProps {
  username: string;
  initial: GetUserProfileResponse;
}

function initialsFor(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  const chars = parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : trimmed.slice(0, 2);
  return chars.toUpperCase();
}

function Avatar({
  url,
  name,
  size,
}: {
  url: string | null;
  name: string;
  size: number;
}): ReactElement {
  if (url) {
    return (
      <img
        className="member-profile__avatar"
        src={url}
        alt=""
        width={size}
        height={size}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className="member-profile__avatar member-profile__avatar--initials"
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initialsFor(name)}
    </span>
  );
}

function formatJoined(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export function MemberProfilePage({ initial }: MemberProfilePageProps): ReactElement {
  const { api, user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const [profile] = useState(initial.profile);
  const [relationship, setRelationship] = useState<ProfileRelationship>(initial.relationship);
  const [counts, setCounts] = useState<ProfileCounts>(initial.counts);
  const [favorites] = useState<UserFavoriteSummary[]>(initial.favorites);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'posts' | 'favorites'>('posts');

  const handlePostCountChange = useCallback((delta: number): void => {
    setCounts((prev) => ({ ...prev, posts: Math.max(0, prev.posts + delta) }));
  }, []);

  const displayName = profile.displayName;
  const isSelf = relationship.isSelf;
  const restricted = initial.access === 'restricted';

  const requireAuthThen = (run: () => void): void => {
    if (!user) {
      openAuthModal({ mode: 'register', intent: 'community' });
      return;
    }
    run();
  };

  const handleFollowToggle = (): void => {
    requireAuthThen(() => {
      setBusy(true);
      setError(null);
      const action = relationship.isFollowing
        ? api.unfollowUser(profile.username)
        : api.followUser(profile.username);
      void action
        .then((result) => {
          setRelationship(result.relationship);
          setCounts(result.counts);
        })
        .catch(() => setError(t('common.profile.actionError')))
        .finally(() => setBusy(false));
    });
  };

  const handleFavoriteToggle = (): void => {
    requireAuthThen(() => {
      setBusy(true);
      setError(null);
      const action = relationship.isFavorite
        ? api.unfavoriteUser(profile.username)
        : api.favoriteUser(profile.username);
      void action
        .then((result) => setRelationship(result.relationship))
        .catch(() => setError(t('common.profile.actionError')))
        .finally(() => setBusy(false));
    });
  };

  return (
    <section className="member-profile">
      <header className="member-profile__header">
        <Avatar url={profile.avatarUrl} name={displayName} size={112} />
        <div className="member-profile__identity">
          <div className="member-profile__name-row">
            <h1 className="member-profile__name">{displayName}</h1>
            <span className="member-profile__badge">{t('common.profile.memberBadge')}</span>
          </div>
          <p className="member-profile__handle">{formatUsername(profile.username)}</p>
          {profile.pronouns ? (
            <p className="member-profile__pronouns">{profile.pronouns}</p>
          ) : null}
          <p className="member-profile__joined">
            {t('common.profile.joinedOn').replace('{{date}}', formatJoined(profile.joinedAt))}
          </p>
        </div>

        {!isSelf ? (
          <div className="member-profile__actions">
            <Button
              label={
                busy
                  ? t('common.profile.followBusy')
                  : relationship.isFollowing
                    ? t('common.profile.unfollow')
                    : t('common.profile.follow')
              }
              variant={relationship.isFollowing ? 'secondary' : 'primary'}
              disabled={busy}
              onClick={handleFollowToggle}
            />
            <Button
              label={
                relationship.isFavorite
                  ? t('common.profile.unfavorite')
                  : t('common.profile.favorite')
              }
              variant="ghost"
              disabled={busy}
              onClick={handleFavoriteToggle}
            />
          </div>
        ) : (
          <div className="member-profile__actions">
            <Link to="/settings" className="member-profile__edit">
              {t('common.profile.editProfile')}
            </Link>
          </div>
        )}
      </header>

      {error ? <p className="fm-error">{error}</p> : null}

      <dl className="member-profile__stats">
        <div>
          <dt>{t('common.profile.posts.count')}</dt>
          <dd>{counts.posts}</dd>
        </div>
        <div>
          <dt>{t('common.profile.followers')}</dt>
          <dd>{counts.followers}</dd>
        </div>
        <div>
          <dt>{t('common.profile.following')}</dt>
          <dd>{counts.following}</dd>
        </div>
        <div>
          <dt>{t('common.profile.favoritesCount')}</dt>
          <dd>{counts.favorites}</dd>
        </div>
      </dl>

      {restricted ? (
        <section className="member-profile__gate">
          <h2>
            {profile.visibility === 'FOLLOWERS'
              ? t('common.profile.restrictedFollowersTitle')
              : t('common.profile.restrictedMembersTitle')}
          </h2>
          <p>
            {(profile.visibility === 'FOLLOWERS'
              ? t('common.profile.restrictedFollowersBody')
              : t('common.profile.restrictedMembersBody')
            ).replace('{{name}}', displayName)}
          </p>
          {!user ? (
            <div className="member-profile__gate-actions">
              <Button
                label={t('common.profile.restrictedJoin')}
                variant="primary"
                onClick={() => openAuthModal({ mode: 'register', intent: 'community' })}
              />
              <Button
                label={t('common.profile.restrictedSignIn')}
                variant="ghost"
                onClick={() => openAuthModal({ mode: 'login' })}
              />
            </div>
          ) : profile.visibility === 'FOLLOWERS' ? (
            <div className="member-profile__gate-actions">
              <Button
                label={
                  busy ? t('common.profile.followBusy') : t('common.profile.follow')
                }
                variant="primary"
                disabled={busy}
                onClick={handleFollowToggle}
              />
            </div>
          ) : null}
        </section>
      ) : (
        <>
          {profile.bio ? <p className="member-profile__bio">{profile.bio}</p> : null}
          {profile.location ? (
            <p className="member-profile__location">{profile.location}</p>
          ) : null}

          <div className="member-profile__tabs" role="tablist" aria-label={displayName}>
            <button
              type="button"
              role="tab"
              id="member-tab-posts"
              aria-selected={activeTab === 'posts'}
              aria-controls="member-panel-posts"
              className={`member-profile__tab${
                activeTab === 'posts' ? ' member-profile__tab--active' : ''
              }`}
              onClick={() => setActiveTab('posts')}
            >
              {t('common.profile.posts.tab')}
            </button>
            <button
              type="button"
              role="tab"
              id="member-tab-favorites"
              aria-selected={activeTab === 'favorites'}
              aria-controls="member-panel-favorites"
              className={`member-profile__tab${
                activeTab === 'favorites' ? ' member-profile__tab--active' : ''
              }`}
              onClick={() => setActiveTab('favorites')}
            >
              {t('common.profile.favoritesTitle')}
            </button>
          </div>

          {activeTab === 'posts' ? (
            <div role="tabpanel" id="member-panel-posts" aria-labelledby="member-tab-posts">
              <ProfilePostsPanel
                username={profile.username}
                isSelf={isSelf}
                onCountChange={handlePostCountChange}
              />
            </div>
          ) : (
            <div
              role="tabpanel"
              id="member-panel-favorites"
              aria-labelledby="member-tab-favorites"
            >
              <section
                className="member-profile__section"
                aria-labelledby="member-favorites-heading"
              >
                <div className="member-profile__section-head">
                  <h2 id="member-favorites-heading">{t('common.profile.favoritesTitle')}</h2>
                  <p>{t('common.profile.favoritesHint').replace('{{name}}', displayName)}</p>
                </div>
                {favorites.length === 0 ? (
                  <p className="member-profile__muted">{t('common.profile.favoritesEmpty')}</p>
                ) : (
                  <ol className="member-profile__favorites">
                    {favorites.map((favorite) => (
                      <li key={favorite.userId} className="member-profile__favorite">
                        <Link
                          to={`/@${favorite.username}`}
                          className="member-profile__favorite-link"
                        >
                          <span className="member-profile__favorite-rank">
                            {t('common.profile.favoriteRank').replace(
                              '{{rank}}',
                              String(favorite.rank),
                            )}
                          </span>
                          <Avatar
                            url={favorite.avatarUrl}
                            name={favorite.displayName}
                            size={44}
                          />
                          <span className="member-profile__favorite-copy">
                            <strong>{favorite.displayName}</strong>
                            <span>{formatUsername(favorite.username)}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </div>
          )}
        </>
      )}
    </section>
  );
}
